'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '../../store/auth-store';
import { checkUniqueLinkAvailability } from '@/src/actions/auth';
import { useDebounce } from '@/src/hooks/use-debounce';
import { FormWrapper } from '../FormWrapper';
import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import { Icon, Spinner } from '@/src/components/ui';
import { HEADER_HEIGHT, uniqueLinkRegex } from '@/src/constants/constants';

const schema = z.object({
  uniqueLink: z
    .string()
    .min(3, 'Your link should be at least 3 characters long')
    .regex(
      uniqueLinkRegex,
      'Your link can only contain letters numbers and hyphens (-)'
    )
    .max(20)
    .nonempty(),
});

type FormData = z.infer<typeof schema>;

export const UniqueLinkStep = ({ onNext }: { onNext: () => void }) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | null>(
    null
  );
  const [hasTyped, setHasTyped] = useState(false);

  const { data, setData } = useAuthStore();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { uniqueLink: data.uniqueLink || '' },
  });

  const uniqueLink = watch('uniqueLink');
  const debouncedValue = useDebounce(uniqueLink, 1000);

  useEffect(() => {
    setData({ ...data, uniqueLink });
  }, [uniqueLink, setData]);

  useEffect(() => {
    if (
      hasTyped &&
      debouncedValue.trim().length < 3 &&
      debouncedValue.trim().length > 0
    ) {
      setError('uniqueLink', {
        type: 'manual',
        message: 'Your link should be at least 3 characters long',
      });
      setStatus('error');
      return;
    }
    if (!hasTyped || debouncedValue.trim().length < 3) {
      clearErrors('uniqueLink');
      setStatus(null);
      return;
    }

    const trimmedDebouncedValue = debouncedValue.trim().toLowerCase();
    setStatus('loading');
    checkUniqueLinkAvailability(trimmedDebouncedValue).then((isTaken) => {
      if (isTaken) {
        setStatus('error');
        setError('uniqueLink', {
          type: 'manual',
          message: 'This username seems to be taken already...',
        });
      } else {
        setStatus('success');
        clearErrors('uniqueLink');
      }
    });
  }, [debouncedValue, hasTyped, setError]);

  useEffect(() => {
    const trimmedValue = debouncedValue.trim().toLowerCase();
    if (trimmedValue.length >= 3) {
      setStatus('loading');
      checkUniqueLinkAvailability(trimmedValue).then((isTaken) => {
        if (isTaken) {
          setStatus('error');
          setError('uniqueLink', {
            type: 'manual',
            message: 'This username seems to be taken already...',
          });
        } else {
          setStatus('success');
          clearErrors('uniqueLink');
        }
      });
    } else {
      clearErrors('uniqueLink');
      setStatus(null);
    }
  }, [debouncedValue, setError, clearErrors]);

  const onSubmit = async () => {
    if (status === 'success') {
      onNext();
    }
  };

  const renderStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Spinner />;
      case 'success':
        return (
          <Icon name="success-input" className="size-5 text-blue-primary" />
        );
      case 'error':
        return <Icon name="error-input" className="size-5 text-red-error" />;
      default:
        return null;
    }
  };

  return (
    <>
      <FormWrapper
        title="Claim your unique link"
        description="You can change it later in settings if available."
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full xl:md:px-28 lg:px-10"
        >
          <Label className="w-full relative flex flex-col items-start text-[16px]">
            <span className="absolute md:top-1 top-1 md:-left-6 -left-5 transform translate-y-1/2 translate-x-1/2 text-gray-800 md:text-[16px] text-xs font-light">
              player3.io/
            </span>
            <Input
              type="text"
              {...register('uniqueLink')}
              onChange={(e) => {
                if (!hasTyped) setHasTyped(true);
                register('uniqueLink').onChange(e);
              }}
              onKeyDown={(e) => {
                if (
                  !uniqueLinkRegex.test(e.key) &&
                  e.key !== 'Backspace' &&
                  e.key !== 'Tab'
                ) {
                  e.preventDefault();
                }
              }}
              className={`border w-full rounded-md text-gray-text placeholder:text-gray-text md:placeholder:text-[16px]! md:h-[51px] h-10 md:pl-[100px] placeholder:pl-[60px] md:placeholder:pl-0 pl-[${HEADER_HEIGHT}px] font-light md:text-[16px] text-xs`}
              placeholder="your-name"
              aria-invalid={!!errors.uniqueLink}
            />
            <div className="absolute md:top-4 md:right-4 top-2.5 right-2">
              {renderStatusIcon()}
            </div>
            {errors.uniqueLink && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.uniqueLink.message}
              </p>
            )}
          </Label>
          <Button
            className="w-full mt-3 md:h-[51px] h-10"
            disabled={status !== 'success' || !!errors.uniqueLink}
          >
            Next
          </Button>
        </form>
        <div className="text-gray-text md:text-sm text-xs md:mt-10 text-center mt-5">
          Already have an account?
          <span
            className="text-blue-primary font-semibold cursor-pointer hover:underline pl-2"
            onClick={() => router.push('/sign-in')}
          >
            Sign in
          </span>
        </div>
      </FormWrapper>
    </>
  );
};
